import { Button } from "@Light/components/page/Button";
import { useScaffold } from "@Light/scaffold";
import type { PlanResponse } from "@Light/services/lightTypes";
import { formatBaseFee, formatChargingRates } from "@Light/utils/evProgram";
import { stripTrailingDecimalZeros } from "@Light/utils/formatting";
import {
  formatTimeAsAmPm,
  planFeaturesForRateGroup,
} from "@Light/utils/planFeatures";
import React from "react";
import { BottomSheet } from "@Light/components/page/BottomSheet";

export type RateDetailsBottomSheetProps = {
  rate: PlanResponse;
  open: boolean;
  onClose: () => void;
};

export const RateDetailsBottomSheet: React.FC<RateDetailsBottomSheetProps> = ({
  rate,
  open,
  onClose,
}) => {
  return (
    <BottomSheet open={open} onClose={onClose} hideCloseButton>
      <RateDetailsBottomSheetContents rate={rate} onClose={onClose} />
    </BottomSheet>
  );
};

type RateDetailsBottomSheetContentsProps = {
  rate: PlanResponse;
  onClose: () => void;
};

const RateDetailsBottomSheetContents: React.FC<
  RateDetailsBottomSheetContentsProps
> = ({ rate, onClose }) => {
  const scaffold = useScaffold();
  const Divided = scaffold.page.divided;
  const { vehicleCharging } = planFeaturesForRateGroup(rate);

  let baseCharge: string | undefined;

  const planMonthlyCost = parseFloat(rate.plan_monthly_cost_dollars);
  if (planMonthlyCost > 0) {
    baseCharge = `$${rate.plan_monthly_cost_dollars}`;
  }

  let flatStorageCreditText: string | undefined;

  const storageCreditMonthlyDollars = parseFloat(
    rate.storage_monthly_credit_dollars,
  );
  if (storageCreditMonthlyDollars > 0) {
    flatStorageCreditText = `-$${rate.storage_monthly_credit_dollars}/mo`;
  }

  let perKwhStorageCreditText: string | undefined;
  const storageCreditPerKwhDollars = parseFloat(
    rate.storage_monthly_credit_per_kwh_dollars ?? "0",
  );
  if (storageCreditPerKwhDollars > 0) {
    perKwhStorageCreditText = `-$${rate.storage_monthly_credit_per_kwh_dollars}/kWh/mo`;
  }

  let exportRateText: string | undefined;

  const exportRateCentsPerKwh = parseFloat(rate.export_rate_cents_per_kwh);
  if (exportRateCentsPerKwh > 0) {
    exportRateText = `${rate.export_rate_cents_per_kwh}¢/kWh`;
  }

  const hasDeliveryFees =
    parseFloat(rate.delivery_base_monthly_dollars) > 0 ||
    parseFloat(rate.delivery_rate_cents_per_kwh) > 0;

  const tableClassName = "border rounded-lg text-xs text-tertiary";

  const kwhRates = [
    {
      label: "500 kWh",
      value: rate.avg_cents_per_kwh_500,
      testId: "rate-display-500",
    },
    {
      label: "1000 kWh",
      value: rate.avg_cents_per_kwh_1000,
      testId: "rate-display-1000",
    },
    {
      label: "2000 kWh",
      value: rate.avg_cents_per_kwh_2000,
      testId: "rate-display-2000",
    },
  ];

  return (
    <div className="flex flex-col gap-2xl py-3xl px-xl expanded:gap-3xl expanded:px-3xl expanded:min-h-full">
      <div className="text-md font-semibold expanded:text-lg">
        Pricing details
      </div>
      <div className="flex flex-col gap-xl items-center">
        <div className="text-sm w-full text-tertiary">
          Average cost based on usage:
        </div>
        <div className="flex flex-wrap gap-6xl items-start justify-center">
          {kwhRates.map(({ label, value, testId }) => (
            <div key={label} className="text-center text-tertiary">
              <div className="text-sm font-semibold" data-testid={testId}>
                {value}¢
              </div>
              <div className="text-xs">{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-sm text-tertiary">
          What's included in average cost
        </div>
        <Divided gap="0" className={tableClassName}>
          {baseCharge && (
            <DetailRow label="Base charge">{baseCharge}/mo</DetailRow>
          )}
          {flatStorageCreditText && (
            <DetailRow label="Battery credit">
              {flatStorageCreditText}
            </DetailRow>
          )}
          {perKwhStorageCreditText && (
            <DetailRow label="Battery credit">
              {perKwhStorageCreditText}
            </DetailRow>
          )}
          <DetailRow label="Energy charge">
            {rate.energy_rate_cents_per_kwh}¢/kWh
          </DetailRow>
          {exportRateText && (
            <DetailRow label="Buyback rate">{exportRateText}</DetailRow>
          )}
          {hasDeliveryFees && (
            <DetailRow label={`${rate.tdu_shortname} fees`}>
              ${rate.delivery_base_monthly_dollars}/mo,{" "}
              {stripTrailingDecimalZeros(rate.delivery_rate_cents_per_kwh)}¢/kWh
            </DetailRow>
          )}
        </Divided>
      </div>
      {vehicleCharging && (
        <div className="flex flex-col gap-2">
          <div className="text-sm text-tertiary">EV charging details</div>
          <Divided gap="0" className={tableClassName}>
            <DetailRow label="Fee">
              {formatBaseFee(vehicleCharging.baseFee)}/mo
            </DetailRow>
            <DetailRow label="Discount rate">
              {formatChargingRates(vehicleCharging.onScheduleRates, {
                literalZero: true,
              })}
            </DetailRow>
            {vehicleCharging.schedules.length > 0 && (
              <DetailRow label="Discount schedule">
                <div className="flex flex-col gap-2">
                  {vehicleCharging.schedules.map((s, i) => (
                    <div key={i}>
                      {s.name ? `${s.name}: ` : ""}
                      {formatTimeAsAmPm(s.start_time)} -{" "}
                      {formatTimeAsAmPm(s.end_time)}
                    </div>
                  ))}
                </div>
              </DetailRow>
            )}
          </Divided>
        </div>
      )}
      <Button className="w-full mt-auto" onClick={onClose}>
        Return to plan
      </Button>
    </div>
  );
};

type DetailRowProps = {
  label: string;
  children: React.ReactNode;
};

const DetailRow: React.FC<DetailRowProps> = ({ label, children }) => {
  return (
    <div className="flex flex-row px-2 py-3">
      <div className="w-2/5">{label}</div>
      <div className="w-3/5">{children}</div>
    </div>
  );
};
